import { Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import React from 'react'
import { Mutation } from 'react-apollo'
import DeleteButton from '../../../common/iconButtons/DeleteButton'
import EditButton from '../../../common/iconButtons/EditButton'
import { alphaSort } from '../../../common/utils'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import { User } from '../types'
import {
  deleteSalesIncentiveProductMutation,
  fetchStationSalesIncentiveProducts,
  salesIncentiveProductsQuery,
  updateSalesIncentiveProductMutation,
} from './incentiveProductQueries'
import css from './incentiveProductTable.css'
import type { SalesIncentiveProduct } from './incentiveProductTypes'

const updateCacheOnDelete =
  (query: any, fieldName: string, id: string, variables: IncentiveProductTableProps['variables']) => (cache: any) => {
    const data = cache.readQuery({
      query,
      variables,
    })
    cache.writeQuery({
      query,
      variables,
      data: {
        ...(data || {}),
        [fieldName]: data[fieldName].filter((item) => item.id !== id),
      },
    })
  }

const getTotalNumberOfEnabledProducts = (data: Array<SalesIncentiveProduct>): number => {
  let total = 0
  data.forEach((item: SalesIncentiveProduct) => {
    if (item.isEnabled) {
      ++total
    }
  })
  return total
}

type IncentiveProductRowProps = {
  data: SalesIncentiveProduct
  stationId?: string
  user: User
  onEdit: (...args: Array<any>) => any
  onDelete: (...args: Array<any>) => any
}

const onIsEnabledToggle = (onMutate: (...args: Array<any>) => any, data: SalesIncentiveProduct) => {
  onMutate({
    variables: { ...data, isEnabled: !data.isEnabled },
  })
}

const IncentiveProductRow = ({ data, onEdit, onDelete, user }: IncentiveProductRowProps) => {
  const permissions = user.permissions || []
  return (
    <TableRow>
      <TableCell className={css.tableCell} data-label={i18n.t('admin.incentiveProducts.label.name')}>
        {data.name}
      </TableCell>
      <TableCell className={css.tableCell} data-label={i18n.t('admin.incentiveProducts.label.ean')}>
        {data.ean}
      </TableCell>
      <TableCell className={css.tableCell} data-label={i18n.t('admin.incentiveProducts.label.isEnabled')}>
        <Mutation mutation={updateSalesIncentiveProductMutation}>
          {(onMutate) => (
            <Switch
              checked={data.isEnabled}
              onChange={() => onIsEnabledToggle(onMutate, data)}
              value='isEnabled'
              color='primary'
            />
          )}
        </Mutation>
      </TableCell>
      <TableCell className={css.tableCell} data-label={i18n.t('admin.incentiveProducts.label.category')}>
        {data.category}
      </TableCell>
      {permissions.indexOf('manage_ownStation') !== -1 ? (
        <TableCell className={css.actionsCell}>
          <EditButton onClick={() => (data.id ? onEdit(omit(data, ['__typename'])) : noop)} />
          <Mutation mutation={deleteSalesIncentiveProductMutation}>
            {(onMutate) => <DeleteButton onClick={() => (data.id ? onDelete(onMutate, data.id) : noop)} />}
          </Mutation>
        </TableCell>
      ) : null}
    </TableRow>
  )
}

type IncentiveProductTableProps = {
  data: Array<SalesIncentiveProduct>
  onEdit: (...args: Array<any>) => any
  variables: { stationId?: string; date?: Date }
}

@withUser
class IncentiveProductTable extends React.PureComponent<IncentiveProductTableProps> {
  onDelete = (onMutate: (...args: Array<any>) => any, id: string) => {
    const { variables } = this.props

    const query = variables.stationId ? fetchStationSalesIncentiveProducts : salesIncentiveProductsQuery
    const fieldName = variables.stationId ? 'stationSalesIncentiveProducts' : 'salesIncentiveProducts'
    onMutate({
      variables: {
        id,
      },
      update: updateCacheOnDelete(query, fieldName, id, variables),
    })
  }

  render() {
    const { data, variables, onEdit, user } = this.props as IncentiveProductTableProps & { user: User }
    const { stationId } = variables

    return (
      <Paper>
        <div className={css.component}>
          <Table className={css.responsiveTable}>
            <TableHead>
              <TableRow>
                <TableCell className={css.tableHeading}>{i18n.t('admin.incentiveProducts.label.name')}</TableCell>
                <TableCell className={css.tableHeading}>{i18n.t('admin.incentiveProducts.label.ean')}</TableCell>
                <TableCell className={css.tableHeading} padding='checkbox'>
                  {i18n.t('admin.incentiveProducts.label.isEnabledTotal', {
                    total: getTotalNumberOfEnabledProducts(data),
                  })}
                </TableCell>
                <TableCell className={css.tableHeading}>{i18n.t('admin.incentiveProducts.label.category')}</TableCell>
                <TableCell className={css.tableHeading} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length ? (
                alphaSort('name', data).map((item) => (
                  <IncentiveProductRow
                    data={item}
                    stationId={stationId}
                    onEdit={onEdit}
                    onDelete={this.onDelete}
                    key={item.id}
                    user={user}
                  />
                ))
              ) : (
                <p className={css.empty}>{i18n.t('admin.incentiveProducts.noProducts')}</p>
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>
    )
  }
}

export default IncentiveProductTable
